import React, { useEffect, useState, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import CustomLoader from '../layouts/CustomLoader'
import AuthContext from '../../middlewares/AuthContext'
import { isPartnerLike } from '../../utilities/roles'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Role      { id: number; name: string }
interface PartnerOpt { id: number; name: string }
interface MktOpt    { id: number; name: string }
interface UserOpt   { id: number; email: string }

interface FormValues {
  email          : string
  password       : string
  passwordConfirm: string
  role           : number
  partner        : number
  marketing      : number
  user           : number
  prepay         : number
}

const formSchema = Yup.object().shape({
  email: Yup.string()
    .email()
    .required(),
  password: Yup.string()
    .min(8, 'Must be 8 characters or more')
    .matches(/[a-z]+/, 'One lowercase character')
    .matches(/[A-Z]+/, 'One uppercase character')
    .matches(/[@$!%*#?&.¡,+¿()]+/, 'One special character')
    .matches(/\d+/, 'One number')
    .required(),
  passwordConfirm: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match'),
  role           : Yup.number().min(1, 'Select a role').required(),
  partner        : Yup.number().min(0),
  marketing      : Yup.number(),
  user           : Yup.number(),
  prepay         : Yup.number().min(0).max(1),
})

const INITIAL_VALUES: FormValues = {
  email          : '',
  password       : '',
  passwordConfirm: '',
  role           : 0,
  partner        : 0,
  marketing      : 0,
  user           : 0,
  prepay         : 1,
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const CreateUser: React.FC = () => {
  const { getHeaders }                    = useContext(AuthContext) as AuthCtx
  const { t }                             = useTranslation()

  const [loaded, setLoaded]               = useState(false)
  const [loading, setLoading]             = useState(false)
  const [roles, setRoles]                 = useState<Role[]>([])
  const [partners, setPartners]           = useState<PartnerOpt[]>([])
  const [marketings, setMarketings]       = useState<MktOpt[]>([])
  const [partnerUsers, setPartnerUsers]   = useState<UserOpt[]>([])

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = await getHeaders()
        const [rolesRes, partnersRes, marketingsRes] = await Promise.all([
          axios.get<{ message: Role[] }>(`${url}users/roles`, { headers }),
          axios.get<{ message: PartnerOpt[] }>(`${url}partners`, { headers }),
          axios.get<{ message: MktOpt[] }>(`${url}marketings/all`, { headers }),
        ])
        setRoles(rolesRes.data.message)
        setPartners(partnersRes.data.message)
        setMarketings(marketingsRes.data.message)
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetchAll()
  }, [])

  const fetchPartnerUsers = async (partnerId: number | string, setFieldValue: (f: string, v: any) => void) => {
    setFieldValue('partner', parseInt(String(partnerId)))
    try {
      const headers = await getHeaders()
      const res     = await axios.get<{ message: UserOpt[] }>(
        `${url}users/partner/${partnerId}`,
        { headers }
      )
      setPartnerUsers(res.data.message ?? [])
    } catch {
      setPartnerUsers([])
    }
  }

  const handleSubmit = async (values: FormValues, { resetForm }: { resetForm: () => void }) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ response: string }>(`${url}users`, values, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
      resetForm()
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  return (
    <div className="row justify-content-center">
      <div className="col-md-8 col-lg-6">

        {/* ── Header card ───────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width           : 40,
              height          : 40,
              borderRadius    : 10,
              backgroundColor : `${BRAND}18`,
              display         : 'flex',
              alignItems      : 'center',
              justifyContent  : 'center',
              flexShrink      : 0,
            }}>
              <FontAwesomeIcon icon={faUserPlus} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              {t('users.create.user_create')}
            </p>
          </div>
        </div>

        {/* ── Form card ─────────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>
            <Formik
              initialValues={INITIAL_VALUES}
              validationSchema={formSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue }) => {
                const role           = parseInt(String(values.role))
                // Role ids are assigned by seed order, not stable across environments --
                // resolve the actual role name instead of comparing hardcoded ids (this
                // was the bug that would've silently hidden the Partner fields for the
                // new partner-multiplatform role).
                const selectedRole   = roles.find(r => r.id === role)
                const showPartner    = isPartnerLike(selectedRole?.name ?? '')
                const showSubPartner = selectedRole?.name === 'subpartner'
                const showMarketing  = selectedRole?.name === 'marketing'

                return (
                  <Form>

                    {/* Email */}
                    <div className="mb-4">
                      <FieldLabel>{t('login.email')}</FieldLabel>
                      <Field type="email" name="email" className="form-control form-control-sm" placeholder="name@example.com" />
                      <ErrorMessage name="email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* Password */}
                    <div className="mb-4">
                      <FieldLabel>{t('login.password')}</FieldLabel>
                      <Field type="password" name="password" className="form-control form-control-sm" />
                      <ErrorMessage name="password" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* Confirm password */}
                    <div className="mb-4">
                      <FieldLabel>{t('login.pass_confirm')}</FieldLabel>
                      <Field type="password" name="passwordConfirm" className="form-control form-control-sm" />
                      <ErrorMessage name="passwordConfirm" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* Role */}
                    <div className="mb-4">
                      <FieldLabel>Rol</FieldLabel>
                      <Field as="select" name="role" className="form-select form-select-sm">
                        <option value={0} disabled>{t('users.create.select_an_option')}</option>
                        {roles.map((r, i) => (
                          <option key={i} value={r.id}>{t(`users.${r.name}`)}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="role" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* ── Partner role ──────────────────────────────── */}
                    {showPartner && (
                      <>
                        <div className="mb-4">
                          <FieldLabel>{t('users.partner')}</FieldLabel>
                          <Field as="select" name="partner" className="form-select form-select-sm text-capitalize">
                            <option value={0} disabled>{t('users.create.select_an_option')}</option>
                            {partners.map((p, i) => (
                              <option key={i} value={p.id} className="text-capitalize">{p.name}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="partner" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="mb-4">
                          <FieldLabel>Payment type</FieldLabel>
                          <Field as="select" name="prepay" className="form-select form-select-sm">
                            <option value={1}>Pos payment</option>
                            <option value={0}>Pre payment</option>
                          </Field>
                        </div>
                      </>
                    )}

                    {/* ── Subpartner role ───────────────────────────── */}
                    {showSubPartner && (
                      <>
                        <div className="mb-4">
                          <FieldLabel>{t('users.partner')}</FieldLabel>
                          <Field
                            as="select"
                            name="partner"
                            className="form-select form-select-sm text-capitalize"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              fetchPartnerUsers(e.target.value, setFieldValue)
                            }
                          >
                            <option value={0} disabled>{t('users.create.select_an_option')}</option>
                            {partners.map((p, i) => (
                              <option key={i} value={p.id} className="text-capitalize">{p.name}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="partner" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                        <div className="mb-4">
                          <FieldLabel>{t('users.subpartner')}</FieldLabel>
                          <Field as="select" name="user" className="form-select form-select-sm">
                            <option value={0} disabled>{t('users.create.select_an_option')}</option>
                            {partnerUsers.map((u, i) => (
                              <option key={i} value={u.id}>{u.email}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="user" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                      </>
                    )}

                    {/* ── Marketing role ────────────────────────────── */}
                    {showMarketing && (
                      <div className="mb-4">
                        <FieldLabel>{t('users.marketing')}</FieldLabel>
                        <Field as="select" name="marketing" className="form-select form-select-sm text-capitalize">
                          <option value={0} disabled>{t('users.create.select_an_option')}</option>
                          {marketings.map((m, i) => (
                            <option key={i} value={m.id} className="text-capitalize">{m.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="marketing" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        display        : 'inline-flex',
                        alignItems     : 'center',
                        justifyContent : 'center',
                        gap            : '0.5rem',
                        width          : '100%',
                        padding        : '0.55rem 1.5rem',
                        borderRadius   : 9999,
                        backgroundColor: loading ? '#94a3b8' : BRAND,
                        color          : '#fff',
                        fontWeight     : 700,
                        fontSize       : '0.875rem',
                        border         : 'none',
                        cursor         : loading ? 'not-allowed' : 'pointer',
                        boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                        transition     : 'filter 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                      onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                    >
                      {loading
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <FontAwesomeIcon icon={faUserPlus} />
                      }
                      <span>{t('users.create.create')}</span>
                    </button>

                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>

      </div>
    </div>
  )
}

export default CreateUser
